import { useEffect, useRef, useState } from 'react'

import { isGoogleMapsReady, waitForGoogleMaps } from 'utils/googleMapsLoader'

type OnLoadCallback = () => void

/**
 * Returns a ref whose `.current` flips to true once the Google Maps API
 * (including the places library) is ready to use. The script tag itself
 * loads asynchronously (?loading=async), so we poll for google.maps.places
 * rather than relying on the script element's load event. Forces a re-render
 * when the ref flips so consumers that read `.current` in render see it.
 */
const useGoogleMapsScriptLoadedRef = (onLoad?: OnLoadCallback) => {
  const loaded = useRef(false)
  const onLoadRef = useRef(onLoad)
  onLoadRef.current = onLoad
  const [, setVersion] = useState(0)

  if (!loaded.current && isGoogleMapsReady()) {
    loaded.current = true
  }

  useEffect(() => {
    if (loaded.current) {
      onLoadRef.current?.()
      return undefined
    }

    let cancelled = false
    waitForGoogleMaps()
      .then(() => {
        if (cancelled) return
        loaded.current = true
        onLoadRef.current?.()
        setVersion((v) => v + 1)
      })
      .catch((err) => {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn('Google Maps failed to load:', err)
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  return loaded
}

export const useGoogleMapsScriptReady = () => {
  const [ready, setReady] = useState(isGoogleMapsReady)

  useEffect(() => {
    if (ready) return undefined

    let cancelled = false
    waitForGoogleMaps()
      .then(() => {
        if (!cancelled) setReady(true)
      })
      .catch((err) => {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn('Google Maps failed to load:', err)
        }
      })

    return () => {
      cancelled = true
    }
  }, [ready])

  return ready
}

export default useGoogleMapsScriptLoadedRef
